/**
 * WHAT:
 *   Validation utility functions for PDF parsing and data validation.
 *
 * WHY:
 *   Centralized validation logic for consistent data validation across the TestTab component.
 *
 * DOMAIN: Investments → Parqet → Test Tab → Utils → Validation
 *
 * DEPENDENCIES:
 *   - TypeScript types (ValidationStatus)
 *
 * RISKS:
 *   - NOTE: Validation logic is based on heuristics - may not catch all edge cases
 *   - NOTE: ISIN validation is minimal - may need more robust checks
 *   - WARN: Backend validation errors may not be fully descriptive
 */

import type { ValidationStatus } from "../types";

/**
 * WHAT:
 *   Central utility function for consistent empty value checking across the component.
 *
 * WHY:
 *   Provides unified logic for checking if values are null, undefined, empty strings, or NaN.
 *
 * INPUTS:
 *   - value: unknown — Value to check for emptiness
 *
 * OUTPUT:
 *   boolean — True if value is considered empty, false otherwise.
 *
 * @param value - Value to check for emptiness
 *
 * @returns {boolean} True if value is considered empty, false otherwise
 *
 */
export const isEmpty = (value: unknown): boolean => {
  return (
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (typeof value === "number" && Number.isNaN(value))
  );
};

/**
 * WHAT:
 *   Validates parsed PDF data using schema and business logic rules.
 *
 * WHY:
 *   Ensures parsed data meets requirements before import to database.
 *
 * INPUTS:
 *   - data: unknown — Parsed PDF data object to validate
 *
 * OUTPUT:
 *   ValidationStatus object with isValid, canImport flags and errors array.
 *
 * BEHAVIOR:
 *   - Checks for required ISIN field
 *   - Evaluates backend validation errors
 *
 * RISKS:
 *   - NOTE: Validation is based on heuristics - may not catch all edge cases
 *   - NOTE: Backend validation errors may not be user-friendly
 *
 * @param data
 *
 */
/**
 * WHAT:
 *   Validates parsed PDF data using schema and business logic rules.
 *
 * WHY:
 *   Ensures parsed data meets requirements before import to database.
 *
 * INPUTS:
 *   - data: unknown — Parsed PDF data object to validate
 *
 * OUTPUT:
 *   ValidationStatus object with isValid, canImport flags and errors array.
 *
 * BEHAVIOR:
 *   - Checks for required ISIN field
 *   - Evaluates backend validation errors
 *
 * RISKS:
 *   - NOTE: Validation is based on heuristics - may not catch all edge cases
 *   - NOTE: Backend validation errors may not be user-friendly
 *
 * @param data - Parsed PDF data object to validate
 *
 * @returns {ValidationStatus} ValidationStatus object with isValid, canImport flags and errors array
 *
 */
export const validateParsedData = (data: unknown): ValidationStatus => {
  const errors: string[] = [];

  // NOTE: Business logic validation - check backend validation errors
  interface DataWithValidation {
    _validation?: {
      errors?: unknown[];
    };
    isin?: string;
  }
  const typedData = data as DataWithValidation;

  // NOTE: Schema validation - ISIN is required field
  const isin = typedData.isin;
  if (!isin || (typeof isin === "string" && isin.trim() === "")) {
    errors.push("Missing ISIN");
  }

  const validationErrors = typedData._validation?.errors;
  if (validationErrors && Array.isArray(validationErrors) && validationErrors.length > 0) {
    // NOTE: Add all validation errors from backend
    for (const error of validationErrors) {
      if (typeof error === "string") {
        errors.push(error);
      }
    }
  }

  const isValid = errors.length === 0;
  const canImport = isValid;

  return { canImport, errors, isValid };
};
